/**
 * Crop-before-upload modal (adapted in spirit from the Cineworld admin). Lets an
 * admin pan/zoom a selected image to a fixed aspect ratio, then returns a JPEG
 * Blob of the cropped region. Built on react-easy-crop + a canvas export.
 */
import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { X, ZoomIn, Crop as CropIcon } from 'lucide-react';
import { Button } from '../ui';

// Local shape of react-easy-crop's pixel area (avoids depending on its exported type name).
interface Area { x: number; y: number; width: number; height: number }

async function getCroppedBlob(imageSrc: string, area: Area): Promise<Blob> {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = reject;
    image.src = imageSrc;
  });
  const canvas = document.createElement('canvas');
  // Export at the FULL native resolution of the cropped region (no downscale),
  // so a high-res source stays crisp in the full-bleed hero.
  canvas.width = Math.round(area.width);
  canvas.height = Math.round(area.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Crop failed'))), 'image/jpeg', 0.95),
  );
}

// Below this cropped width the hero (full-bleed, ~1900px and more on hi-DPI/4K)
// will visibly upscale → soft. Warn the admin so they pick a larger source.
const MIN_CRISP_WIDTH = 1600;

export function ImageCropper({
  imageSrc,
  aspectRatio = 16 / 9,
  onCropComplete,
  onCancel,
}: {
  imageSrc: string;
  aspectRatio?: number;
  onCropComplete: (blob: Blob) => void;
  onCancel: () => void;
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);
  const outW = pixels ? Math.round(pixels.width) : 0;

  const onDone = useCallback((_area: Area, areaPixels: Area) => setPixels(areaPixels), []);

  const confirm = async () => {
    if (!pixels) return;
    setBusy(true);
    try {
      onCropComplete(await getCroppedBlob(imageSrc, pixels));
    } catch {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[210] grid place-items-center bg-overlay p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl overflow-hidden rounded-card border border-line bg-surface shadow-pop">
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <div className="flex items-center gap-2">
            <CropIcon className="size-4 text-brand-600" />
            <h3 className="type-body-strong text-content">Crop banner image</h3>
            <span className="rounded-pill bg-brand-50 px-2 py-0.5 type-caption-strong text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
              16:9
            </span>
          </div>
          <button onClick={onCancel} aria-label="Close" className="grid size-8 place-items-center rounded-control text-muted hover:bg-surface-2 hover:text-content">
            <X className="size-4" />
          </button>
        </div>

        <div className="relative h-[400px] bg-black">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onDone}
            objectFit="contain"
            showGrid
          />
        </div>

        <div className="border-t border-line px-5 py-4">
          <div className="mb-4 flex items-center gap-3">
            <ZoomIn className="size-4 shrink-0 text-muted" />
            <input
              type="range" min={1} max={3} step={0.1} value={zoom} aria-label="Zoom"
              onChange={(e) => setZoom(Number(e.target.value))}
              className="h-1.5 flex-1 accent-brand-600"
            />
            <span className="w-10 text-right text-xs text-muted">{zoom.toFixed(1)}×</span>
          </div>
          {pixels && (
            <p className={outW < MIN_CRISP_WIDTH
              ? 'mb-3 rounded-control bg-warning-500/10 px-3 py-2 text-xs text-warning-600'
              : 'mb-3 text-xs text-muted'}>
              Output: <strong>{outW}×{Math.round(pixels.height)}px</strong>.{' '}
              {outW < MIN_CRISP_WIDTH
                ? 'Low for a full-width hero — it may look soft on large screens. Use a source of at least 1920px wide (avoid zooming in).'
                : 'Good resolution for the hero.'}
            </p>
          )}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="default" onClick={onCancel}>Cancel</Button>
            <Button variant="primary" icon={<CropIcon />} loading={busy} disabled={!pixels} onClick={confirm}>
              Crop &amp; use
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
